import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useUserStore } from '../store';
import { isValidBusinessId, normalizeRegistryCity } from '../utils/ytj';

// The YTJ proxy talks to PRH's open data API (10 s server-side timeout), so give
// the request a little more room than that before giving up on the browser side.
const YTJ_LOOKUP_TIMEOUT_MS = 10000;

// Maventa's /v1/lookup/receivers is a fast registry hit; it either answers quickly
// or something is wrong.
const VERKKOLASKU_LOOKUP_TIMEOUT_MS = 10000;

// Customer fields the YTJ company record can fill. PRH publishes the registered
// name and the visiting/postal address; it has no contact person, email or phone.
const YTJ_AUTOFILL_FIELDS = ['company_name', 'address_street', 'address_postal_code', 'address_city'];

const isBlank = (value) => !value?.trim?.();

/**
 * Company-customer lookups by Y-tunnus, shared by every place a customer can be
 * created: YTJ (PRH) fills the registered name and address, Maventa the e-invoice
 * address.
 *
 * `customer` is the current draft and `applyUpdates(updates)` merges fetched fields
 * into it. Whatever the user typed wins over the register: a YTJ lookup writes only
 * into blank fields and into fields an earlier YTJ lookup filled and the user has
 * left alone, so switching to another Y-tunnus replaces the previous company's
 * details instead of mixing two companies. A result banner belongs to the Y-tunnus
 * it was fetched for, so it disappears as soon as the ID changes or the draft is reset.
 */
export function useCompanyLookup({ t, customer, applyUpdates }) {
  const { currentOrganization } = useUserStore();
  const maventaEnabled = currentOrganization?.maventa_enabled || false;

  const [ytjLoading, setYtjLoading] = useState(false);
  const [ytjResult, setYtjResult] = useState(null);
  const [verkkolaskuLoading, setVerkkolaskuLoading] = useState(false);
  const [verkkolaskuResult, setVerkkolaskuResult] = useState(null);

  // The lookups await the network; read the draft as it is when the answer arrives,
  // not as it was when the button was clicked.
  const latestCustomer = useRef(customer);
  latestCustomer.current = customer;

  const businessId = customer.business_id || '';
  const businessIdValid = isValidBusinessId(businessId);
  const isCompany = customer.type === 'company';

  // Maventa knows the e-invoice address of any company registered with an operator,
  // so offer the lookup once we have a Y-tunnus and the address is still blank.
  const canLookupVerkkolasku =
    isCompany && maventaEnabled && businessIdValid && isBlank(customer.verkkolasku_address);

  // The user edited the Y-tunnus while the request was in flight: the answer
  // describes some other company, so drop it.
  const isStale = (requestedId) => (latestCustomer.current.business_id || '') !== requestedId;

  // Only the newest YTJ request may clear the spinner; an older, superseded one
  // finishing late must not hide the progress of the one still running.
  const ytjRequestSeq = useRef(0);

  // Field -> value as the last YTJ lookup wrote it. A field still holding that value
  // is the register's, not the user's, and the next lookup may replace it.
  const ytjFilled = useRef({});

  const isRegisterOwned = (current, field) =>
    isBlank(current[field]) || (field in ytjFilled.current && current[field] === ytjFilled.current[field]);

  // Write the register's values into the fields it owns. A value the register no
  // longer has (or a company it does not know) clears what the previous lookup
  // put there, so no detail of the old company is left behind.
  const applyRegisterValues = (values) => {
    const current = latestCustomer.current;
    const updates = {};
    Object.entries(values).forEach(([field, value]) => {
      if (!isRegisterOwned(current, field)) return;
      const next = value || '';
      if (next) {
        ytjFilled.current[field] = next;
      } else {
        delete ytjFilled.current[field];
      }
      if ((current[field] || '') !== next) updates[field] = next;
    });
    if (Object.keys(updates).length > 0) applyUpdates(updates);
    return updates;
  };

  const runYtjFetch = async (requestedId) => {
    const seq = ++ytjRequestSeq.current;
    setYtjLoading(true);
    setYtjResult(null);

    try {
      const response = await axios.get('/api/ytj-lookup/', {
        params: { business_id: requestedId, scope: 'customer' },
        timeout: YTJ_LOOKUP_TIMEOUT_MS,
      });
      if (isStale(requestedId)) return;
      const data = response.data || {};

      const updates = applyRegisterValues({
        company_name: data.name,
        address_street: data.address_street,
        address_postal_code: data.address_postal_code,
        address_city: normalizeRegistryCity(data.address_city),
        address_country: data.address_country,
      });
      const filledAnything = YTJ_AUTOFILL_FIELDS.some((field) => field in updates);

      setYtjResult({
        businessId: requestedId,
        severity: 'success',
        message: filledAnything
          ? t('Company details fetched from YTJ: {{company}}', { company: data.name || requestedId })
          : t('YTJ had no new details to add for this company'),
      });
    } catch (error) {
      if (isStale(requestedId)) return;
      // Never surface the raw backend/axios message - map to a translated one.
      let message = t('Company lookup failed. Please fill in the details manually');
      if (error.code === 'ECONNABORTED') {
        message = t('The company register did not answer in time. Try again or fill in the details manually');
      } else if (error.response?.status === 404) {
        // Not in the register: the previous company's details do not belong here.
        applyRegisterValues(Object.fromEntries([...YTJ_AUTOFILL_FIELDS, 'address_country'].map((f) => [f, ''])));
        message = t('No company found with this Business ID. Private traders are often missing from the register - please fill in the details manually');
      } else if (error.response?.status === 400) {
        message = t('Check the Business ID (format 1234567-8)');
      }
      setYtjResult({ businessId: requestedId, severity: 'warning', message });
    } finally {
      if (seq === ytjRequestSeq.current) setYtjLoading(false);
    }
  };

  const fetchFromYtj = () => runYtjFetch(businessId);

  // Fetch as soon as a complete, valid Y-tunnus has been typed, so nobody has to
  // press the button first. Only on typing: opening an existing customer for
  // editing never triggers a lookup on its own. The button stays for refetches.
  const lastAutoFetchedId = useRef(null);
  const onBusinessIdTyped = (newId) => {
    if (newId === lastAutoFetchedId.current) return;
    if (!isCompany || !isValidBusinessId(newId)) return;
    lastAutoFetchedId.current = newId;
    runYtjFetch(newId);
  };

  const lookupVerkkolasku = async () => {
    const requestedId = businessId;
    setVerkkolaskuLoading(true);
    setVerkkolaskuResult(null);

    try {
      const response = await axios.get('/api/verkkolasku/lookup/', {
        params: { business_id: requestedId },
        timeout: VERKKOLASKU_LOOKUP_TIMEOUT_MS,
      });
      if (isStale(requestedId)) return;

      const match = response.data?.success ? response.data.addresses?.[0] : null;
      if (match?.address) {
        applyUpdates({
          verkkolasku_address: match.address,
          verkkolasku_operator: match.operator || latestCustomer.current.verkkolasku_operator || '',
        });
        setVerkkolaskuResult({
          businessId: requestedId,
          severity: 'success',
          message: match.name
            ? t('E-invoice address found for {{company}}', { company: match.name })
            : t('E-invoice address found'),
        });
      } else {
        setVerkkolaskuResult({
          businessId: requestedId,
          severity: 'warning',
          message: t('No e-invoice address registered for this Business ID. The customer may not receive e-invoices'),
        });
      }
    } catch (error) {
      if (isStale(requestedId)) return;
      // Never surface the raw backend message - map the code to a translated one.
      let message = t('E-invoice address lookup failed. Try again later or fill it in manually');
      if (error.code === 'ECONNABORTED') {
        message = t('The e-invoice lookup did not answer in time. Try again or fill it in manually');
      } else if (error.response?.data?.error_code === 'maventa_not_enabled') {
        message = t('Enable e-invoicing in settings before looking up e-invoice addresses');
      }
      setVerkkolaskuResult({ businessId: requestedId, severity: 'warning', message });
    } finally {
      setVerkkolaskuLoading(false);
    }
  };

  // Stable, so a dialog can call it from its open/reset effect.
  const resetLookups = useCallback(() => {
    setYtjResult(null);
    setVerkkolaskuResult(null);
    ytjFilled.current = {};
    lastAutoFetchedId.current = null;
  }, []);

  return {
    businessIdValid,
    ytjLoading,
    ytjResult: ytjResult?.businessId === businessId ? ytjResult : null,
    fetchFromYtj,
    onBusinessIdTyped,
    dismissYtjResult: () => setYtjResult(null),
    canLookupVerkkolasku,
    verkkolaskuLoading,
    verkkolaskuResult: verkkolaskuResult?.businessId === businessId ? verkkolaskuResult : null,
    lookupVerkkolasku,
    dismissVerkkolaskuResult: () => setVerkkolaskuResult(null),
    resetLookups,
  };
}
